import { Button } from '@invent/wl-ui-kit-next/ui';
import {
  BriefcaseBusiness,
  Building,
  PieChart,
  Handshake,
  Landmark,
  ShieldCheck,
  Users
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import type {
  IAdminSecAdvFirmDetail,
  ISecAdvAddress,
  ISecAdvCatalogue,
  ISecAdvCustody
} from '../../api/sec-adv-contracts';
import { formatDay } from '../../components/days';
import { firmTabPath } from '../../components/firm-link';
import { codeText, formatCount, formatDollars, yesNo } from '../../components/sec-adv-values';
import { ClientDonut, ClientTypeName, Swatch, clientCount } from './charts';
import { DISCLOSURE_PILLS, ITEM_7A, answeredYes, type IFirmFigures } from './firm-data';
import { Figure, NoClients, PILL, Panel, Pills } from './panel';
import {
  Dash,
  ERA_NOTE,
  ITEM_5E,
  ITEM_5G,
  SourceLink,
  optionLabel,
  part1AKey,
  yesNoOptions
} from './register';

/**
 * A firm's Overview (SR-423): a two-column grid of cards from its latest filing — client breakdown,
 * business, financials, clients, services, compliance and affiliates. Every Form ADV value links to
 * its answer in the filing.
 */
export function FirmOverview({
  firm,
  figures,
  catalogue
}: {
  firm: IAdminSecAdvFirmDetail;
  figures: IFirmFigures;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 [&>*]:min-w-0">
      <BreakdownCard figures={figures} />
      <FinancialsCard firm={firm} figures={figures} />
      <BusinessCard firm={firm} figures={figures} catalogue={catalogue} />
      <ServicesCard firm={firm} figures={figures} catalogue={catalogue} />
      <ClientsCard firm={firm} figures={figures} />
      <ComplianceCard firm={firm} catalogue={catalogue} />
      <AffiliatesCard firm={firm} catalogue={catalogue} />
    </div>
  );
}

function BreakdownCard({ figures }: { figures: IFirmFigures }) {
  const withAum = figures.types.some((type) => (type.raum ?? 0) > 0);
  return (
    <Panel
      title="Client breakdown by AUM"
      icon={PieChart}
      help="Regulatory assets under management by type of client, Form ADV Item 5.D(3)."
    >
      {withAum ? (
        <ClientDonut types={figures.types} />
      ) : (
        <NoClients isEra={figures.isEra} said="No client AUM reported." />
      )}
    </Panel>
  );
}

function BusinessCard({
  firm,
  figures,
  catalogue
}: {
  firm: IAdminSecAdvFirmDetail;
  figures: IFirmFigures;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  const { summary } = firm;
  const crd = firm.firmCrd;
  const organization = summary.organization;
  const code = (value: string, path: string, name: string) => codeText(catalogue, { path, name }, value);
  return (
    <Panel title="Business" icon={Building} help="Form ADV Items 1 and 3.">
      <div className="grid gap-4 sm:grid-cols-2">
        <Figure label="Principal address">
          {summary.mainAddress ? (
            <SourceLink firmCrd={crd} source="MainAddr">
              <AddressLines address={summary.mainAddress} />
            </SourceLink>
          ) : (
            <Dash />
          )}
        </Figure>
        <div className="grid content-start gap-4">
          <Figure label="Phone">
            {summary.mainAddress?.phone ? (
              <SourceLink firmCrd={crd} source="MainAddr.PhNb">
                {summary.mainAddress.phone}
              </SourceLink>
            ) : (
              <Dash />
            )}
          </Figure>
          <Figure label="Other offices">
            {figures.otherOffices !== null ? (
              <SourceLink firmCrd={crd} source="FormInfo.Part1A.Item1.Q1F5">
                {formatCount(figures.otherOffices)}
              </SourceLink>
            ) : (
              <Dash />
            )}
          </Figure>
        </div>
        {summary.mailingAddress && (
          <Figure label="Mailing address">
            <SourceLink firmCrd={crd} source="MailingAddr">
              <AddressLines address={summary.mailingAddress} />
            </SourceLink>
          </Figure>
        )}
        <Figure label="Legal name">
          <SourceLink firmCrd={crd} source="Info.LegalNm">
            {firm.legalName}
          </SourceLink>
        </Figure>
        <Figure label="Business name">
          {firm.businessName ? (
            <SourceLink firmCrd={crd} source="Info.BusNm">
              {firm.businessName}
            </SourceLink>
          ) : (
            <Dash />
          )}
        </Figure>
        <Figure label="Form of organization">
          {organization?.form ? (
            <SourceLink firmCrd={crd} source="FormInfo.Part1A.Item3A.OrgFormNm">
              {code(organization.form, 'FormInfo.Part1A.Item3A', 'OrgFormNm')}
              {organization.formOther && ` — ${organization.formOther}`}
            </SourceLink>
          ) : (
            <Dash />
          )}
        </Figure>
        <Figure label="Organized in">
          {organization?.state || organization?.country ? (
            <SourceLink firmCrd={crd} source="FormInfo.Part1A.Item3C">
              {[organization.state, organization.country].filter(Boolean).join(', ')}
            </SourceLink>
          ) : (
            <Dash />
          )}
        </Figure>
        <Figure label="Fiscal year ends">
          {organization?.fiscalYearEnd ? (
            <SourceLink firmCrd={crd} source="FormInfo.Part1A.Item3B.Q3B">
              {code(organization.fiscalYearEnd, 'FormInfo.Part1A.Item3B', 'Q3B')}
            </SourceLink>
          ) : (
            <Dash />
          )}
        </Figure>
        <Figure label="Websites" className="sm:col-span-2">
          <Websites crd={crd} websites={summary.websites} />
        </Figure>
      </div>
    </Panel>
  );
}

function AddressLines({ address }: { address: ISecAdvAddress }) {
  const locality = [address.city, [address.state, address.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const lines = [address.street1, address.street2, locality, address.country].filter(
    (line): line is string => Boolean(line)
  );
  if (lines.length === 0) return <Dash />;
  return (
    <span className="grid">
      {lines.map((line, index) => (
        <span key={index}>{line}</span>
      ))}
    </span>
  );
}

const WEBSITES_SHOWN = 3;

function Websites({ crd, websites }: { crd: string; websites: string[] }) {
  const [all, setAll] = React.useState(false);
  if (websites.length === 0) return <Dash />;
  const shown = all ? websites : websites.slice(0, WEBSITES_SHOWN);
  return (
    <div className="grid gap-1">
      <ul aria-label="Websites" className="grid gap-0.5">
        {shown.map((site, index) => (
          <li key={index} className="break-all">
            <SourceLink firmCrd={crd} source="FormInfo.Part1A.Item1.WebAddrs">
              {site}
            </SourceLink>
          </li>
        ))}
      </ul>
      {websites.length > WEBSITES_SHOWN && (
        <div>
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setAll(!all)}>
            {all ? 'Show fewer' : `+${websites.length - WEBSITES_SHOWN} more`}
          </Button>
        </div>
      )}
    </div>
  );
}

function FinancialsCard({
  firm,
  figures
}: {
  firm: IAdminSecAdvFirmDetail;
  figures: IFirmFigures;
}) {
  const crd = firm.firmCrd;
  const era = figures.isEra ? ERA_NOTE : undefined;
  const money = (value: Nullable<number>, name: string) =>
    value !== null ? (
      <SourceLink firmCrd={crd} source={`FormInfo.Part1A.Item5F.${name}`}>
        {formatDollars(value)}
      </SourceLink>
    ) : (
      <Dash title={era} />
    );
  const count = (value: Nullable<number>, name: string) =>
    value !== null ? (
      <SourceLink firmCrd={crd} source={`FormInfo.Part1A.Item5F.${name}`}>
        {formatCount(value)}
      </SourceLink>
    ) : (
      <Dash />
    );
  return (
    <Panel
      title="Financials"
      icon={Landmark}
      help="Regulatory assets under management and accounts, Form ADV Item 5.F(2)."
    >
      <div className="grid grid-cols-2 gap-4 tabular-nums">
        <Figure label="Total AUM">{money(figures.totalAum, 'Q5F2C')}</Figure>
        <Figure label="Accounts">{count(figures.totalAccounts, 'Q5F2F')}</Figure>
        <Figure label="Discretionary">{money(figures.discretionaryAum, 'Q5F2A')}</Figure>
        <Figure label="Accounts">{count(figures.discretionaryAccounts, 'Q5F2D')}</Figure>
        <Figure label="Non-discretionary">{money(figures.nonDiscretionaryAum, 'Q5F2B')}</Figure>
        <Figure label="Accounts">{count(figures.nonDiscretionaryAccounts, 'Q5F2E')}</Figure>
      </div>
    </Panel>
  );
}

function ClientsCard({ firm, figures }: { firm: IAdminSecAdvFirmDetail; figures: IFirmFigures }) {
  const crd = firm.firmCrd;
  const other = figures.types.find((type) => type.description !== null);
  return (
    <Panel
      title="Clients"
      icon={Users}
      help="Clients and their regulatory assets under management by type, Form ADV Item 5.D."
      action={
        <Link to={firmTabPath(crd, 'analytics')} className="text-[var(--color-sapphire)] hover:underline">
          View more in Analytics →
        </Link>
      }
    >
      {figures.types.length === 0 ? (
        <NoClients isEra={figures.isEra} />
      ) : (
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 gap-y-2 tabular-nums">
          <span className="text-[0.6875rem] tracking-wide text-muted-foreground uppercase">Client type</span>
          <span className="text-right text-[0.6875rem] tracking-wide text-muted-foreground uppercase">
            Clients
          </span>
          <span className="text-right text-[0.6875rem] tracking-wide text-muted-foreground uppercase">
            AUM
          </span>
          {figures.types.map((type) => (
            <React.Fragment key={type.type}>
              <span className="flex min-w-0 items-center gap-2">
                <Swatch color={type.color} />
                <ClientTypeName type={type} />
              </span>
              <span className="text-right">
                <SourceLink firmCrd={crd} source={type.source}>
                  {clientCount(type)}
                </SourceLink>
              </span>
              <span className="text-right">
                {type.raum !== null ? (
                  <SourceLink firmCrd={crd} source={type.source.replace(/1$/, '3')}>
                    {formatDollars(type.raum)}
                  </SourceLink>
                ) : (
                  <Dash />
                )}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
      {other?.description && (
        <Figure label="Description of other clients">{other.description}</Figure>
      )}
    </Panel>
  );
}

function ServicesCard({
  firm,
  figures,
  catalogue
}: {
  firm: IAdminSecAdvFirmDetail;
  figures: IFirmFigures;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  const crd = firm.firmCrd;
  const services = yesNoOptions(catalogue, ITEM_5G);
  const compensation = yesNoOptions(catalogue, ITEM_5E);
  return (
    <Panel
      title="Services"
      icon={BriefcaseBusiness}
      help="Form ADV Items 5.G (advisory services) and 5.E (compensation)."
      action={
        // The brochures are the SEC's: our Filings tab does not hold ADV Part 2 yet.
        <a
          href={`https://adviserinfo.sec.gov/firm/summary/${encodeURIComponent(crd)}`}
          target="_blank"
          rel="noreferrer"
          className="text-[var(--color-sapphire)] hover:underline"
        >
          ADV Part 2 brochures ↗
        </a>
      }
    >
      <Figure label={<SourceLink firmCrd={crd} source={ITEM_5G}>Services offered</SourceLink>}>
        {figures.services.length === 0 ? (
          <Dash />
        ) : (
          <Pills
            label="Services offered"
            values={figures.services
              .filter((code) => code !== 'Q5G12' || figures.servicesOther === null)
              .map((code) => optionLabel(services, code))}
          />
        )}
      </Figure>
      {figures.servicesOther && <Figure label="Other services">{figures.servicesOther}</Figure>}
      <Figure label={<SourceLink firmCrd={crd} source={ITEM_5E}>Compensation types</SourceLink>}>
        {figures.compensation.length === 0 ? (
          <Dash />
        ) : (
          <Pills
            label="Compensation types"
            values={figures.compensation
              .filter((code) => code !== 'Q5E7' || figures.compensationOther === null)
              .map((code) => optionLabel(compensation, code))}
          />
        )}
      </Figure>
      {figures.compensationOther && (
        <Figure label="Other compensation">{figures.compensationOther}</Figure>
      )}
    </Panel>
  );
}

function ComplianceCard({
  firm,
  catalogue
}: {
  firm: IAdminSecAdvFirmDetail;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  const { summary } = firm;
  const crd = firm.firmCrd;
  const disclosures = summary.disclosures.filter((name) => name !== 'Q11');
  const code = (value: string, path: string, name: string) => codeText(catalogue, { path, name }, value);
  const states = summary.noticeFilings
    .map((filing) => filing.state)
    .filter((state): state is string => Boolean(state));
  return (
    <Panel
      title="Compliance"
      icon={ShieldCheck}
      help={
        "Registration from the SEC's records; disclosures from Form ADV Item 11; state notice " +
        'filings from Item 2.C; custody from Items 9.A and 9.B.'
      }
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <Figure label="Registration status">
          {summary.registrationStatus ? code(summary.registrationStatus, 'Rgstn', 'St') : <Dash />}
        </Figure>
        <Figure label="Status since">
          {summary.registrationDate ? formatDay(summary.registrationDate) : <Dash />}
        </Figure>
        <Figure label="SEC regional office">
          {summary.secRegion ? code(summary.secRegion, 'Info', 'SECRgnCD') : <Dash />}
        </Figure>
      </div>
      <Figure label="Disclosures">
        {disclosures.length === 0 ? (
          'None'
        ) : (
          <ul aria-label="Disclosures" className="flex flex-wrap gap-1.5">
            {disclosures.map((name) => (
              <li key={name}>
                <SourceLink
                  firmCrd={crd}
                  source={part1AKey(catalogue, name) ?? 'FormInfo.Part1A.Item11'}
                  className={`inline-block ${PILL} no-underline`}
                >
                  {DISCLOSURE_PILLS[name] ?? name}
                </SourceLink>
              </li>
            ))}
          </ul>
        )}
      </Figure>
      <Figure label="State notice filings">
        {states.length === 0 ? (
          'None'
        ) : (
          <ul aria-label="State notice filings" className="flex flex-wrap gap-1">
            {states.map((state, index) => (
              <li key={`${state} ${index}`}>
                <SourceLink
                  firmCrd={crd}
                  source="NoticeFiled.States"
                  className="inline-block min-w-9 rounded-full border border-border bg-white px-2 py-0.5 text-center text-xs no-underline"
                >
                  {state}
                </SourceLink>
              </li>
            ))}
          </ul>
        )}
      </Figure>
      <div className="grid gap-4 sm:grid-cols-2">
        <Figure label="Custody — the firm">
          <CustodyLine crd={crd} custody={summary.custody} source="FormInfo.Part1A.Item9A" />
        </Figure>
        <Figure label="Custody — related persons">
          <CustodyLine
            crd={crd}
            custody={summary.relatedPersonCustody}
            source="FormInfo.Part1A.Item9B"
          />
        </Figure>
      </div>
    </Panel>
  );
}

function CustodyLine({
  crd,
  custody,
  source
}: {
  crd: string;
  custody: Nullable<ISecAdvCustody>;
  source: string;
}) {
  if (custody === null) return <Dash />;
  const held = custody.cash === true || custody.securities === true;
  if (!held) {
    return (
      <SourceLink firmCrd={crd} source={source}>
        {custody.cash === null && custody.securities === null ? '–' : 'No custody'}
      </SourceLink>
    );
  }
  const what = [custody.cash && 'cash', custody.securities && 'securities']
    .filter(Boolean)
    .join(' and ');
  return (
    <SourceLink firmCrd={crd} source={source}>
      <span className="grid">
        <span>{yesNo(true)} — {what}</span>
        {custody.amount !== null && <span>{formatDollars(custody.amount)}</span>}
        {custody.clients !== null && <span>{formatCount(custody.clients)} clients</span>}
      </span>
    </SourceLink>
  );
}

function AffiliatesCard({
  firm,
  catalogue
}: {
  firm: IAdminSecAdvFirmDetail;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  const custodians = firm.custodians?.custodians.length ?? 0;
  const related = answeredYes(firm.record, catalogue, ITEM_7A).length;
  const total = custodians + related;
  return (
    <Panel
      title="Affiliates"
      icon={Handshake}
      help="Custodians from Schedule D 5.K(3) and related-person types from Form ADV Item 7.A."
      action={
        <Link
          to={firmTabPath(firm.firmCrd, 'affiliates')}
          className="text-[var(--color-sapphire)] hover:underline"
        >
          View all →
        </Link>
      }
    >
      <Figure label="Affiliates">
        {total === 0
          ? 'No business affiliates reported.'
          : `Reported ${total} business ${total === 1 ? 'affiliate' : 'affiliates'}.`}
      </Figure>
    </Panel>
  );
}
